import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Button, Box, Typography } from '@mui/material';
import axiosInstance from '../../utils/api';
import Login from '../login/login';
import PrivateRoute from '../../auth/PrivateRoute';
import PropertyQuestionnaire from '../forms/PropertyQuestionnaire';
import QuestionnaireModal from '../../components/QuestionnaireModal';
import { isAuthenticated } from '../../auth/Auth';
import '../../utils/api.js'
import { checkTokenExpiry } from '../../utils/auth';
import PostLoginNavigation from '../../components/PostLoginNavigation';
import { QuestionnaireProvider } from '../../components/QuestionnaireContext';
import PasswordResetPage from '../login/PasswordReset';
import ActivationPage from '../login/ActivationPage';
import AdminDashboard from '../admin/AdminDashboard';
import MFASettings from '../admin/MFASettings';
import RiskAnalysis from '../admin/RiskAnalysis';
import QuestionnaireSchema from '../admin/QuestionnaireSchema';
import RequireMFA from '../../auth/RequireMFA';



function App() {
  // State to determine if the modal completed
  const [modalCompleted, setModalCompleted] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isAuthed, setIsAuthed] = useState(isAuthenticated());

  useEffect(() => {
    checkTokenExpiry();

    // Check for token expiration every minute
    const intervalId = setInterval(checkTokenExpiry, 60000); 
    return () => clearInterval(intervalId);
  }, []); 

  useEffect(() => {
    const fetchStaff = async () => {
      if (!isAuthed) return;
      try {
        const response = await axiosInstance.get('/api/mfa/status/');
        setIsStaff(!!response.data.is_staff);
      } catch (error) {
        setIsStaff(false);
      }
    };
    fetchStaff();
  }, [isAuthed]);

  useEffect(() => {
    const handleAuthChange = () => {
      setIsAuthed(isAuthenticated());
    };
    window.addEventListener('authchange', handleAuthChange);
    return () => window.removeEventListener('authchange', handleAuthChange);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qid = params.get('questionnaire_id') || sessionStorage.getItem('selectedQuestionnaireId');
    if (qid) {
      setModalCompleted(true);
    }
  }, []);

  // Function to handle modal completion 
  const handleModalClose = () => {
    setModalCompleted(true);
  };


  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.dispatchEvent(new Event('authchange'));
    window.location.href = '/login';
  };

  return (

    <QuestionnaireProvider>
    <Router>
      {isAuthed && (
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <Typography variant="h6" sx={{ mr: 2 }}>
              Property Questionnaire
            </Typography>
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              {isStaff ? (
                <Button color="inherit" href="/questionnaire-schema">Questionnaire Questions</Button>
              ) : (
                <Button color="inherit" href="/property-questionnaire">Questionnaires</Button>
              )}
              <Button color="inherit" href="/admin-dashboard">Admin Dashboard</Button>
              <Button color="inherit" href="/risk-analysis">Risk Analysis</Button>
              <Button color="inherit" href="/mfa-settings">MFA Settings</Button>
            </Box>
            <Button color="inherit" onClick={handleLogout}>Logout</Button>
          </Toolbar>
        </AppBar>
      )}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route 
          path="/property-questionnaire"
          element={
            <PrivateRoute>
              {!modalCompleted ? (
                <QuestionnaireModal isOpen={true} onClose={handleModalClose} />
              ) : (
                <PropertyQuestionnaire />
              )}
            </PrivateRoute>
          }
        />
        <Route
          path="/admin-dashboard"
          element={
            <PrivateRoute>
              <RequireMFA>
                <AdminDashboard />
              </RequireMFA>
            </PrivateRoute>
          }
        />
        <Route
          path="/risk-analysis"
          element={
            <PrivateRoute>
              <RequireMFA>
                <RiskAnalysis />
              </RequireMFA>
            </PrivateRoute>
          }
        />
        <Route
          path="/questionnaire-schema"
          element={
            <PrivateRoute>
              <RequireMFA>
                <QuestionnaireSchema />
              </RequireMFA>
            </PrivateRoute>
          }
        />
        <Route
          path="/mfa-settings"
          element={
            <PrivateRoute>
              <MFASettings />
            </PrivateRoute>
          }
        />
        <Route path="/password-reset-confirm/:uidb64/:token/" element={<PasswordResetPage />} />
        <Route path="/activate/:uidb64/:token/" element={<ActivationPage />} />
        <Route
          path="*"
          element={<Navigate to={isAuthenticated() ? "/property-questionnaire" : "/login"} replace />}
        />
      </Routes>
    </Router>
    </QuestionnaireProvider>
  );
}

export default App;
